import { useCallback, useEffect, useRef, useState } from 'react'
import { Card, CardContent, Button, Input, Label } from '@/components/ui'
import { Save, RotateCcw, RefreshCw, Plus, Trash2, X, Copy, Pencil, Search, AlertTriangle, Database } from 'lucide-react'
import { type ZoneQuotazione } from '@/data/zone-quotazioni'
import {
  fetchZoneQuotazioni,
  fetchCoefficientiExtra,
  saveZoneQuotazioni,
  saveCoefficientiExtra,
  deleteZone,
  invalidateCache,
  fetchLastUpdatedAt,
} from '@/services/zone-quotazioni.service'
import { OMI_LOOKUP, OMI_COMUNI, getZoneByComune, searchOmiZone, type OmiLookupEntry } from '@/data/omi-lookup-cagliari'

interface CoefficientiExtra {
  box: number
  postoAuto: number
  piscina: number
  residence: number
}

const COEFFICIENTI_LABELS: Record<string, string> = {
  box: 'Box auto',
  postoAuto: 'Posto auto',
  piscina: 'Piscina',
  residence: 'Residence',
}

// Colonne percentuali (salvate come decimale, mostrate come %)
const PERCENT_FIELDS: (keyof ZoneQuotazione)[] = [
  'posizioneBonus',
  'pertinenzaBonus',
  'esposizioneBonus',
  'spaziEsterniBonus',
  'senzaAscensore',
]

const COLUMN_HEADERS: { key: keyof ZoneQuotazione; label: string; isPercent: boolean }[] = [
  { key: 'prezzoAbitabile', label: 'Abitabile €/mq', isPercent: false },
  { key: 'prezzoRistrutturato', label: 'Ristrutturato €/mq', isPercent: false },
  { key: 'prezzoDaRistrutturare', label: 'Da Ristrutt. €/mq', isPercent: false },
  { key: 'posizioneBonus', label: 'Posizione %', isPercent: true },
  { key: 'pertinenzaBonus', label: 'Pertinenza %', isPercent: true },
  { key: 'esposizioneBonus', label: 'Esposizione %', isPercent: true },
  { key: 'spaziEsterniBonus', label: 'Spazi Est. %', isPercent: true },
  { key: 'senzaAscensore', label: 'No Ascensore %', isPercent: true },
  { key: 'casa', label: 'Casa (molt.)', isPercent: false },
  { key: 'villa', label: 'Villa (molt.)', isPercent: false },
]

const DEFAULT_NEW_ZONE: Omit<ZoneQuotazione, 'nome'> = {
  codiceOMI: '',
  prezzoAbitabile: 1200,
  prezzoRistrutturato: 1600,
  prezzoDaRistrutturare: 800,
  posizioneBonus: 0.06,
  pertinenzaBonus: 0.05,
  esposizioneBonus: 0.05,
  spaziEsterniBonus: 0.05,
  senzaAscensore: -0.05,
  casa: 1.1,
  villa: 1.25,
}

interface QuotazioniEditorProps {
  onDirtyChange?: (isDirty: boolean) => void
}

export function QuotazioniEditor({ onDirtyChange }: QuotazioniEditorProps) {
  const [zones, setZones] = useState<ZoneQuotazione[]>([])
  const [coefficienti, setCoefficienti] = useState<CoefficientiExtra>({
    box: 0.025,
    postoAuto: 0.01,
    piscina: 0.10,
    residence: 0.05,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)

  // Dirty tracking
  const savedZonesRef = useRef<string>('')
  const savedCoeffRef = useRef<string>('')
  const [isDirty, setIsDirty] = useState(false)

  // Ricerca zone
  const [searchQuery, setSearchQuery] = useState('')

  // Rinomina zona
  const [renamingZone, setRenamingZone] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  // Timestamp ultima modifica
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null)

  // Modal nuova zona (anche per duplica)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [newZoneName, setNewZoneName] = useState('')
  const [newZoneData, setNewZoneData] = useState<Omit<ZoneQuotazione, 'nome'>>({ ...DEFAULT_NEW_ZONE })

  // OMI lookup per auto-populate
  const [omiSearchQuery, setOmiSearchQuery] = useState('')
  const [selectedComune, setSelectedComune] = useState('')
  const [omiResults, setOmiResults] = useState<OmiLookupEntry[]>([])
  const [isOmiMode, setIsOmiMode] = useState(true) // default: usa lookup OMI

  const loadData = async (forceRefresh = false) => {
    setIsLoading(true)
    try {
      if (forceRefresh) invalidateCache()
      const [z, c, ts] = await Promise.all([fetchZoneQuotazioni(), fetchCoefficientiExtra(), fetchLastUpdatedAt()])
      setZones(z)
      setCoefficienti(c)
      setLastUpdatedAt(ts)
      savedZonesRef.current = JSON.stringify(z)
      savedCoeffRef.current = JSON.stringify(c)
      setIsDirty(false)
    } catch {
      setFeedback({ type: 'error', msg: 'Errore nel caricamento dei dati' })
    } finally {
      setIsLoading(false)
    }
  }

  // Dirty check: confronta stato attuale con snapshot salvato
  const checkDirty = useCallback(() => {
    const dirty =
      JSON.stringify(zones) !== savedZonesRef.current ||
      JSON.stringify(coefficienti) !== savedCoeffRef.current
    setIsDirty(dirty)
    onDirtyChange?.(dirty)
  }, [zones, coefficienti, onDirtyChange])

  useEffect(() => {
    checkDirty()
  }, [checkDirty])

  // Previeni chiusura pagina con modifiche non salvate
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [isDirty])

  useEffect(() => {
    loadData()
  }, [])

  const showFeedback = (type: 'success' | 'error', msg: string) => {
    setFeedback({ type, msg })
    setTimeout(() => setFeedback(null), 4000)
  }

  const handleZoneChange = (index: number, field: keyof ZoneQuotazione, rawValue: string) => {
    // Campo vuoto → 0 esplicito (evita valore "fantasma" stale)
    const numValue = rawValue.trim() === '' ? 0 : parseFloat(rawValue)
    if (!Number.isFinite(numValue)) return

    const value = PERCENT_FIELDS.includes(field) ? numValue / 100 : numValue

    setZones((prev) =>
      prev.map((z, i) => (i === index ? { ...z, [field]: value } : z))
    )
  }

  const handleCoeffChange = (key: keyof CoefficientiExtra, rawValue: string) => {
    const numValue = rawValue.trim() === '' ? 0 : parseFloat(rawValue)
    if (!Number.isFinite(numValue)) return
    setCoefficienti((prev) => ({ ...prev, [key]: numValue / 100 }))
  }

  const handleSave = async () => {
    setIsSaving(true)
    setFeedback(null)
    try {
      await saveZoneQuotazioni(zones)
      await saveCoefficientiExtra(coefficienti)
      savedZonesRef.current = JSON.stringify(zones)
      savedCoeffRef.current = JSON.stringify(coefficienti)
      setIsDirty(false)
      onDirtyChange?.(false)
      showFeedback('success', 'Quotazioni salvate con successo!')
    } catch {
      showFeedback('error', 'Errore nel salvataggio. Riprova.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddZone = async () => {
    const trimmedName = newZoneName.trim()
    if (!trimmedName) {
      showFeedback('error', 'Inserisci un nome per la zona')
      return
    }
    if (zones.some((z) => z.nome.toLowerCase() === trimmedName.toLowerCase())) {
      showFeedback('error', `La zona "${trimmedName}" esiste già`)
      return
    }

    setIsSaving(true)
    setFeedback(null)

    try {
      const completeZone: ZoneQuotazione = {
        nome: trimmedName,
        ...newZoneData,
      }

      const updatedZones = [...zones, completeZone].sort((a, b) =>
        a.nome.localeCompare(b.nome, 'it')
      )

      await saveZoneQuotazioni(updatedZones)
      await loadData(true)

      setIsAddModalOpen(false)
      setNewZoneName('')
      setNewZoneData({ ...DEFAULT_NEW_ZONE })
      showFeedback('success', `Zona "${trimmedName}" aggiunta con successo!`)
    } catch {
      showFeedback('error', "Errore nell'aggiunta della zona. Riprova.")
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteZone = async (zoneName: string) => {
    if (!window.confirm(`Sei sicuro di voler eliminare la zona "${zoneName}"?\n\nQuesta azione non può essere annullata.`)) {
      return
    }

    setIsSaving(true)
    setFeedback(null)

    try {
      await deleteZone(zoneName)
      await loadData(true)
      showFeedback('success', `Zona "${zoneName}" eliminata con successo!`)
    } catch {
      showFeedback('error', "Errore nell'eliminazione della zona. Riprova.")
    } finally {
      setIsSaving(false)
    }
  }

  // Rinomina zona (locale, richiede salvataggio)
  const handleRenameZone = async (oldName: string) => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === oldName) {
      setRenamingZone(null)
      return
    }
    if (zones.some((z) => z.nome.toLowerCase() === trimmed.toLowerCase() && z.nome !== oldName)) {
      showFeedback('error', `La zona "${trimmed}" esiste già`)
      return
    }

    // Rinomina in DB: elimina vecchia, salva con nuovo nome
    setIsSaving(true)
    try {
      await deleteZone(oldName)
      const updatedZones = zones.map((z) =>
        z.nome === oldName ? { ...z, nome: trimmed } : z
      ).sort((a, b) => a.nome.localeCompare(b.nome, 'it'))
      await saveZoneQuotazioni(updatedZones)
      await loadData(true)
      setRenamingZone(null)
      showFeedback('success', `Zona rinominata: "${oldName}" → "${trimmed}"`)
    } catch {
      showFeedback('error', 'Errore nella rinomina. Riprova.')
    } finally {
      setIsSaving(false)
    }
  }

  // Duplica zona: apre modal pre-compilata con dati della zona esistente
  const handleDuplicateZone = (zone: ZoneQuotazione) => {
    const { nome, ...data } = zone
    setNewZoneName(`${nome} (copia)`)
    setNewZoneData(data)
    setIsOmiMode(false)
    setIsAddModalOpen(true)
  }

  // Seleziona zona dal lookup OMI → auto-popola tutti i campi
  const handleSelectOmiZone = (entry: OmiLookupEntry) => {
    const { nome, comune, provincia, ...data } = entry
    setNewZoneName(nome)
    setNewZoneData(data)
    setOmiSearchQuery('')
    setOmiResults([])
  }

  // Aggiorna risultati OMI quando cambia il filtro
  useEffect(() => {
    if (!isAddModalOpen || !isOmiMode) return
    const existingNames = zones.map(z => z.nome)
    const existingSet = new Set(existingNames.map(n => n.toLowerCase()))

    let results: OmiLookupEntry[] = []
    if (selectedComune) {
      results = getZoneByComune(selectedComune)
    } else if (omiSearchQuery.trim()) {
      results = searchOmiZone(omiSearchQuery)
    }
    // Filtra le zone già presenti
    setOmiResults(results.filter(z => !existingSet.has(z.nome.toLowerCase())))
  }, [omiSearchQuery, selectedComune, isAddModalOpen, isOmiMode, zones])

  // Validazione coerenza prezzi: warning se da_ristrutturare >= abitabile o abitabile >= ristrutturato
  const getPriceWarnings = (zone: ZoneQuotazione): string[] => {
    const warnings: string[] = []
    if (zone.prezzoDaRistrutturare >= zone.prezzoAbitabile) {
      warnings.push('Da ristrutturare >= Abitabile')
    }
    if (zone.prezzoAbitabile >= zone.prezzoRistrutturato) {
      warnings.push('Abitabile >= Ristrutturato')
    }
    return warnings
  }

  const displayValue = (zone: ZoneQuotazione, field: keyof ZoneQuotazione, isPercent: boolean) => {
    const val = zone[field] as number
    if (isPercent) return Math.round(val * 10000) / 100 // 0.12 → 12
    return val
  }

  // Filtra zone per ricerca
  const filteredZones = zones.filter((z) =>
    z.nome.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const isNewZoneNameValid = newZoneName.trim().length > 0 &&
    !zones.some((z) => z.nome.toLowerCase() === newZoneName.trim().toLowerCase())

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <RefreshCw className="h-10 w-10 animate-spin text-primary-600 mb-4" />
        <p className="text-gray-500">Caricamento quotazioni...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900">
            Quotazioni per Zona ({zones.length} zone)
          </h2>
          {isDirty && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 rounded-full">
              Modifiche non salvate
            </span>
          )}
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setNewZoneName('')
              setNewZoneData({ ...DEFAULT_NEW_ZONE })
              setOmiSearchQuery('')
              setSelectedComune('')
              setOmiResults([])
              setIsOmiMode(true)
              setIsAddModalOpen(true)
            }}
            disabled={isSaving}
          >
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi Zona
          </Button>
          <Button variant="outline" onClick={() => loadData(true)} disabled={isSaving}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Ripristina
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Salvataggio...' : 'Salva modifiche'}
          </Button>
        </div>
      </div>

      {/* Ricerca zone */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca zona..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      {/* Feedback */}
      {feedback && (
        <div
          className={`p-3 rounded-lg text-sm font-medium ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}
        >
          {feedback.msg}
        </div>
      )}

      {/* Zone table */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="text-left p-3 font-medium text-gray-700 sticky left-0 bg-gray-50 min-w-[180px]">
                    Zona
                  </th>
                  {COLUMN_HEADERS.map((col) => (
                    <th key={col.key} className="text-center p-3 font-medium text-gray-700 min-w-[120px]">
                      {col.label}
                    </th>
                  ))}
                  <th className="text-center p-3 font-medium text-gray-700 w-[100px]">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filteredZones.map((zone) => {
                  const realIndex = zones.findIndex((z) => z.nome === zone.nome)
                  const priceWarnings = getPriceWarnings(zone)
                  return (
                    <tr key={zone.nome} className={`border-b hover:bg-gray-50/50 ${priceWarnings.length > 0 ? 'bg-amber-50/30' : ''}`}>
                      <td className="p-3 font-medium text-gray-900 sticky left-0 bg-white">
                        {renamingZone === zone.nome ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') handleRenameZone(zone.nome)
                                if (e.key === 'Escape') setRenamingZone(null)
                              }}
                              className="w-full px-2 py-1 text-sm border border-primary-300 rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
                              autoFocus
                            />
                            <button
                              onClick={() => handleRenameZone(zone.nome)}
                              className="p-1 text-primary-600 hover:bg-primary-50 rounded"
                              title="Conferma"
                            >
                              <Save className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => setRenamingZone(null)}
                              className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                              title="Annulla"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span>{zone.nome}</span>
                            {priceWarnings.length > 0 && (
                              <span title={priceWarnings.join('\n')}>
                                <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      {COLUMN_HEADERS.map((col) => (
                        <td key={col.key} className="p-1.5">
                          <input
                            type="number"
                            step={col.isPercent ? '0.5' : col.key === 'casa' || col.key === 'villa' ? '0.01' : '50'}
                            value={displayValue(zone, col.key, col.isPercent)}
                            onChange={(e) => handleZoneChange(realIndex, col.key, e.target.value)}
                            className="w-full px-2 py-1.5 text-center text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                          />
                        </td>
                      ))}
                      <td className="p-1.5">
                        <div className="flex items-center justify-center gap-0.5">
                          <button
                            onClick={() => {
                              setRenamingZone(zone.nome)
                              setRenameValue(zone.nome)
                            }}
                            disabled={isSaving}
                            className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors disabled:opacity-50"
                            title={`Rinomina ${zone.nome}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDuplicateZone(zone)}
                            disabled={isSaving}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                            title={`Duplica ${zone.nome}`}
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteZone(zone.nome)}
                            disabled={isSaving}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                            title={`Elimina ${zone.nome}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {filteredZones.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_HEADERS.length + 2} className="p-8 text-center text-gray-500">
                      Nessuna zona trovata per "{searchQuery}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Coefficienti Extra */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Coefficienti Extra (fissi, non zona-specifici)
        </h3>
        <Card className="border-0 shadow-md">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {(Object.keys(coefficienti) as (keyof CoefficientiExtra)[]).map((key) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {COEFFICIENTI_LABELS[key] || key}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="0.5"
                      value={Math.round(coefficienti[key] * 10000) / 100}
                      onChange={(e) => handleCoeffChange(key, e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timestamp ultima modifica */}
      {lastUpdatedAt && (
        <p className="text-xs text-gray-400 text-right">
          Ultimo aggiornamento: {new Date(lastUpdatedAt).toLocaleString('it-IT', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
          })}
        </p>
      )}

      {/* Modal Aggiungi Zona */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Aggiungi Nuova Zona</h3>
                <p className="text-sm text-gray-500 mt-1">
                  La nuova zona sarà visibile nel form di valutazione
                </p>
              </div>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Toggle modalità: OMI automatico vs Manuale */}
              <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                <button
                  onClick={() => { setIsOmiMode(true); setNewZoneName(''); setNewZoneData({ ...DEFAULT_NEW_ZONE }) }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    isOmiMode ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Database className="h-4 w-4" />
                  Da dati OMI (automatico)
                </button>
                <button
                  onClick={() => { setIsOmiMode(false); setOmiSearchQuery(''); setSelectedComune(''); setOmiResults([]) }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    !isOmiMode ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Pencil className="h-4 w-4" />
                  Inserimento manuale
                </button>
              </div>

              {/* OMI Lookup */}
              {isOmiMode && (
                <div className="space-y-3">
                  <div>
                    <Label>Cerca zona OMI</Label>
                    <p className="text-xs text-gray-500 mb-2">
                      Seleziona un comune o cerca per nome zona. I prezzi si compileranno automaticamente dai dati OMI.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <select
                          value={selectedComune}
                          onChange={(e) => { setSelectedComune(e.target.value); setOmiSearchQuery('') }}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="">-- Seleziona comune --</option>
                          {OMI_COMUNI.map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>
                      </div>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Oppure cerca per nome..."
                          value={omiSearchQuery}
                          onChange={(e) => { setOmiSearchQuery(e.target.value); setSelectedComune('') }}
                          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Risultati OMI */}
                  {omiResults.length > 0 && (
                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {omiResults.map((entry) => (
                        <button
                          key={`${entry.comune}-${entry.codiceOMI}-${entry.nome}`}
                          onClick={() => handleSelectOmiZone(entry)}
                          className={`w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors ${
                            newZoneName === entry.nome ? 'bg-primary-50 border-l-2 border-primary-500' : ''
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <span className="font-medium text-gray-900 text-sm">{entry.nome}</span>
                              <span className="ml-2 text-xs text-gray-500">({entry.codiceOMI})</span>
                            </div>
                            <span className="text-sm font-semibold text-primary-600">
                              {entry.prezzoAbitabile} €/mq
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            Rist. {entry.prezzoRistrutturato} €/mq · Da rist. {entry.prezzoDaRistrutturare} €/mq
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {(selectedComune || omiSearchQuery.trim()) && omiResults.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-3">
                      Nessuna zona disponibile {selectedComune ? `per ${selectedComune}` : 'per questa ricerca'}
                      {' '}(tutte già aggiunte o nessun risultato)
                    </p>
                  )}

                  {/* Info: quante zone disponibili */}
                  <p className="text-xs text-gray-400">
                    {OMI_LOOKUP.length} zone OMI disponibili in {OMI_COMUNI.length} comuni
                  </p>
                </div>
              )}

              {/* Nome zona (compilato automaticamente se OMI, editabile se manuale) */}
              <div>
                <Label htmlFor="newZoneName" required>Nome Zona</Label>
                <Input
                  id="newZoneName"
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder={isOmiMode ? 'Seleziona una zona OMI qui sopra...' : 'Es: Via Roma (tratto alto)'}
                  error={
                    newZoneName.trim().length > 0 && zones.some((z) => z.nome.toLowerCase() === newZoneName.trim().toLowerCase())
                      ? 'Nome zona già esistente'
                      : undefined
                  }
                />
                {isOmiMode && newZoneName && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Database className="h-3 w-3" />
                    Prezzi compilati automaticamente dai dati OMI
                  </p>
                )}
              </div>

              {/* Prezzi base */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Prezzi base (€/mq)</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="newPrezzoAbitabile">Abitabile</Label>
                    <Input
                      id="newPrezzoAbitabile"
                      type="number"
                      step="50"
                      value={newZoneData.prezzoAbitabile}
                      onChange={(e) => setNewZoneData({ ...newZoneData, prezzoAbitabile: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPrezzoRistrutturato">Ristrutturato</Label>
                    <Input
                      id="newPrezzoRistrutturato"
                      type="number"
                      step="50"
                      value={newZoneData.prezzoRistrutturato}
                      onChange={(e) => setNewZoneData({ ...newZoneData, prezzoRistrutturato: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newPrezzoDaRistrutturare">Da ristrutturare</Label>
                    <Input
                      id="newPrezzoDaRistrutturare"
                      type="number"
                      step="50"
                      value={newZoneData.prezzoDaRistrutturare}
                      onChange={(e) => setNewZoneData({ ...newZoneData, prezzoDaRistrutturare: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
              </div>

              {/* Bonus/Malus percentuali */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Bonus e malus (%)</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="newPosizione">Posizione</Label>
                    <div className="relative">
                      <Input
                        id="newPosizione"
                        type="number"
                        step="0.5"
                        value={Math.round(newZoneData.posizioneBonus * 10000) / 100}
                        onChange={(e) => setNewZoneData({ ...newZoneData, posizioneBonus: (parseFloat(e.target.value) || 0) / 100 })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="newPertinenza">Pertinenza</Label>
                    <Input
                      id="newPertinenza"
                      type="number"
                      step="0.5"
                      value={Math.round(newZoneData.pertinenzaBonus * 10000) / 100}
                      onChange={(e) => setNewZoneData({ ...newZoneData, pertinenzaBonus: (parseFloat(e.target.value) || 0) / 100 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newEsposizione">Esposizione</Label>
                    <Input
                      id="newEsposizione"
                      type="number"
                      step="0.5"
                      value={Math.round(newZoneData.esposizioneBonus * 10000) / 100}
                      onChange={(e) => setNewZoneData({ ...newZoneData, esposizioneBonus: (parseFloat(e.target.value) || 0) / 100 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newSpaziEsterni">Spazi Esterni</Label>
                    <Input
                      id="newSpaziEsterni"
                      type="number"
                      step="0.5"
                      value={Math.round(newZoneData.spaziEsterniBonus * 10000) / 100}
                      onChange={(e) => setNewZoneData({ ...newZoneData, spaziEsterniBonus: (parseFloat(e.target.value) || 0) / 100 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newSenzaAscensore">Senza Ascensore</Label>
                    <Input
                      id="newSenzaAscensore"
                      type="number"
                      step="0.5"
                      value={Math.round(newZoneData.senzaAscensore * 10000) / 100}
                      onChange={(e) => setNewZoneData({ ...newZoneData, senzaAscensore: (parseFloat(e.target.value) || 0) / 100 })}
                    />
                  </div>
                </div>
              </div>

              {/* Moltiplicatori tipo immobile */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Moltiplicatori tipo immobile</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="newCasa">Casa / Villetta</Label>
                    <Input
                      id="newCasa"
                      type="number"
                      step="0.01"
                      value={newZoneData.casa}
                      onChange={(e) => setNewZoneData({ ...newZoneData, casa: parseFloat(e.target.value) || 1 })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="newVilla">Villa</Label>
                    <Input
                      id="newVilla"
                      type="number"
                      step="0.01"
                      value={newZoneData.villa}
                      onChange={(e) => setNewZoneData({ ...newZoneData, villa: parseFloat(e.target.value) || 1 })}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <Button
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isSaving}
              >
                Annulla
              </Button>
              <Button
                onClick={handleAddZone}
                disabled={!isNewZoneNameValid || isSaving}
              >
                <Plus className="h-4 w-4 mr-2" />
                {isSaving ? 'Salvataggio...' : 'Aggiungi Zona'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
